import {
    ExpandMore as ExpandMoreIcon,
    Restaurant as RestaurantIcon
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Paper,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { DailyMealPlan } from '../services/dietProgramApi';
import { colors } from '../theme';

interface DailyMealPlansViewProps {
  dailyMealPlans?: DailyMealPlan[];
  isClient?: boolean;
}

const DailyMealPlansView: React.FC<DailyMealPlansViewProps> = ({
  dailyMealPlans = [],
  isClient = false,
}) => {
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());

  const toggleDayExpanded = (date: string) => {
    setExpandedDays((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  if (dailyMealPlans.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: colors.background.light,
          textAlign: 'center',
        }}
      >
        <RestaurantIcon
          sx={{ fontSize: 48, color: colors.text.secondary, mb: 1 }}
        />
        <Typography variant="body1" color="text.secondary">
          Henüz günlük yemek planı eklenmemiş.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        mb={2}
        sx={{
          pb: 1.5,
          borderBottom: `2px solid ${colors.primary.light}`,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: colors.primary.light,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RestaurantIcon sx={{ fontSize: 20, color: colors.primary.main }} />
        </Box>
        <Typography variant="h6" fontWeight={600}>
          Günlük Yemek Planları
        </Typography>
        <Chip
          label={`${dailyMealPlans.length} Gün`}
          size="small"
          sx={{
            ml: 'auto',
            width: 'fit-content',
            bgcolor: colors.primary.light,
            color: colors.primary.main,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: isClient ? '500px' : '600px',
          overflowY: 'auto',
        }}
      >
        {dailyMealPlans.map((dailyPlan) => {
          const date = new Date(dailyPlan.date);
          const dateStr = date.toLocaleDateString('tr-TR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
          const isExpanded = expandedDays.has(dailyPlan.date);
          const totalMeals = dailyPlan.meals.length;
          const totalCalories = dailyPlan.meals.reduce((sum, meal) => {
            return (
              sum +
              meal.items.reduce((mealSum, item) => mealSum + item.calories, 0)
            );
          }, 0);

          return (
            <Accordion
              key={dailyPlan.date}
              expanded={isExpanded}
              onChange={() => toggleDayExpanded(dailyPlan.date)}
              sx={{
                boxShadow: 'none',
                border: `1px solid ${colors.border.light}`,
                borderRadius: 2,
                mb: 1,
                '&:before': { display: 'none' },
                '&:hover': {
                  borderColor: colors.border.main,
                  bgcolor: colors.background.light,
                },
                '&.Mui-expanded': {
                  borderColor: colors.border.main,
                  bgcolor: 'white',
                },
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: colors.text.secondary }} />}
                sx={{
                  px: 1.5,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                  '&.Mui-expanded': {
                    bgcolor: 'transparent',
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {dateStr}
                  </Typography>
                  <Chip
                    label={`${totalMeals} Öğün`}
                    size="small"
                    sx={{
                      bgcolor: colors.primary.light,
                      color: colors.primary.main,
                    }}
                  />
                  {totalCalories > 0 && (
                    <Chip
                      label={`${totalCalories} kcal`}
                      size="small"
                      sx={{
                        bgcolor: colors.alpha.primary10,
                        color: colors.primary.dark,
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Günlük Notlar */}
                  {dailyPlan.dailyNotes && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: colors.background.light,
                        border: `1px solid ${colors.border.light}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Günlük Notlar:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {dailyPlan.dailyNotes}
                      </Typography>
                    </Box>
                  )}

                  {/* Öğünler */}
                  {dailyPlan.meals.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Bu gün için henüz öğün eklenmemiş.
                    </Typography>
                  ) : (
                    dailyPlan.meals.map((meal, mealIndex) => {
                      const mealCalories = meal.items.reduce(
                        (sum, item) => sum + item.calories,
                        0
                      );
                      const mealProtein = meal.items.reduce(
                        (sum, item) => sum + item.protein,
                        0
                      );
                      const mealCarbs = meal.items.reduce(
                        (sum, item) => sum + item.carbohydrates,
                        0
                      );
                      const mealFat = meal.items.reduce(
                        (sum, item) => sum + item.fat,
                        0
                      );

                      return (
                        <Paper
                          key={mealIndex}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            border: `1px solid ${colors.border.light}`,
                            borderRadius: 1.5,
                            bgcolor: colors.background.light,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={600}>
                              {meal.mealType}
                            </Typography>
                            {mealCalories > 0 && (
                              <Chip
                                label={`${mealCalories} kcal`}
                                size="small"
                                sx={{
                                  bgcolor: colors.primary.light,
                                  color: colors.primary.main,
                                }}
                              />
                            )}
                          </Box>

                          {meal.items.length === 0 ? (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: 'italic' }}
                            >
                              Bu öğün için henüz besin eklenmemiş.
                            </Typography>
                          ) : (
                            <>
                              {meal.items.map((item, itemIndex) => (
                                <Box
                                  key={itemIndex}
                                  sx={{
                                    p: 1.25,
                                    mb: 0.75,
                                    bgcolor: 'white',
                                    borderRadius: 1,
                                    border: `1px solid ${colors.border.light}`,
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={600}>
                                    {item.foodName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    {item.quantity} {item.unit} • {item.calories}{' '}
                                    kcal
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      gap: 1,
                                      mt: 0.75,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Chip
                                      label={`P: ${item.protein}g`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: colors.alpha.primary10,
                                      }}
                                    />
                                    <Chip
                                      label={`K: ${item.carbohydrates}g`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: colors.alpha.primary10,
                                      }}
                                    />
                                    <Chip
                                      label={`Y: ${item.fat}g`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: colors.alpha.primary10,
                                      }}
                                    />
                                  </Box>
                                  {item.notes && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}
                                    >
                                      Not: {item.notes}
                                    </Typography>
                                  )}
                                </Box>
                              ))}

                              {/* Öğün Özeti */}
                              {meal.items.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    pt: 1.5,
                                    borderTop: `1px solid ${colors.border.light}`,
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={600}>
                                    Toplam:
                                  </Typography>
                                  <Typography variant="caption">
                                    {mealCalories} kcal
                                  </Typography>
                                  <Typography variant="caption">•</Typography>
                                  <Typography variant="caption">
                                    P: {mealProtein.toFixed(1)}g
                                  </Typography>
                                  <Typography variant="caption">•</Typography>
                                  <Typography variant="caption">
                                    K: {mealCarbs.toFixed(1)}g
                                  </Typography>
                                  <Typography variant="caption">•</Typography>
                                  <Typography variant="caption">
                                    Y: {mealFat.toFixed(1)}g
                                  </Typography>
                                </Box>
                              )}
                            </>
                          )}

                          {meal.notes && (
                            <Box
                              sx={{
                                mt: 1.5,
                                p: 1,
                                borderRadius: 1,
                                bgcolor: colors.alpha.primary05,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic' }}
                              >
                                {meal.notes}
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      );
                    })
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Paper>
  );
};

export default DailyMealPlansView;

